import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';

import { KEBAB } from '../icons';

const ARROWS = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };

// How long a finger rests on a cell before its value menu opens, and how far
// it may drift before the press is a scroll instead.
export const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 10;

/**
 * The table's value menu, as a cell sees it (useCellValueMenu): `null` where a
 * table has none (a related list, the tag page), else
 * `{ canOpen(target), open(target) }` with `target = { item, columnKey, cellEl, x, y }`.
 * `open` answers whether a menu opened, so a cell with nothing to offer leaves
 * the browser's own context menu alone.
 */
export const CellMenuContext = createContext(null);

// Controls inside a cell keep the browser's own menu (a field being typed in).
const FORM_CONTROLS = 'input, textarea, select, [contenteditable="true"]';

/**
 * One value cell of a single-card table: focusable, arrow-navigable, and -
 * when the reader may change it - the door to the element's own input. A
 * read-only cell says why on hover (and to a screen reader), and keeps the
 * row's own click (select the item) for itself; an editable one takes the
 * click (rich text too - its editor is a modal). A row a collection link
 * brought in is editable at home, not here: its click opens the item.
 *
 * The cell also opens its VALUE MENU (KPR-1640, ruling i07: Filter by this
 * value, Exclude this value, Copy value) on a right-click, the Menu key or
 * Shift+F10, a long press on touch, and a small ⋯ in its corner that is drawn
 * only while the cell is hovered or holds focus - never at rest, so the row's
 * own ⋯ stays the one ⋯ a row shows (platform-standards section 3). A cell in
 * edit mode, a saving cell and a control inside the cell keep the browser's.
 */
export default function TableCell({ tableId, rowIndex, colIndex, editability, pending, editing = false, item = null, columnKey = null, onOpen, onOpenItem, children }) {
	const menu = useContext(CellMenuContext);
	const ref = useRef(null);
	const [active, setActive] = useState(false);
	const press = useRef({ timer: null, x: 0, y: 0, fired: false });
	const editable = Boolean(editability?.editable) && !pending;
	const reason = editability && !editability.editable ? editability.reason : null;
	const menuOn = Boolean(menu) && !editing && !pending;
	const classes = [
		'card-table__cell',
		editable ? 'card-table__cell--editable' : '',
		reason ? 'card-table__cell--readonly' : '',
		pending ? 'card-table__cell--pending' : '',
	].filter(Boolean).join(' ');

	useEffect(() => () => clearTimeout(press.current.timer), []);
	// A cell that goes saving or into edit mode mid-press does not open a menu.
	useEffect(() => { if (!menuOn) { clearTimeout(press.current.timer); press.current.timer = null; } }, [menuOn]);

	const openMenu = useCallback((x, y) => (
		menuOn && menu.open({ item, columnKey, cellEl: ref.current, x, y })
	), [menuOn, menu, item, columnKey]);
	// Where a keyboard-opened menu sits: under the cell's start.
	const cellCorner = () => {
		const rect = ref.current?.getBoundingClientRect();
		return rect ? [rect.left + 8, rect.bottom] : [0, 0];
	};

	const handleKeyDown = (event) => {
		if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
			if (openMenu(...cellCorner())) {
				event.preventDefault();
				event.stopPropagation();
			}
			return;
		}
		const move = ARROWS[event.key];
		if (move && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
			// The list's ↑↓ (move the selected item) stays the list's
			// everywhere else; on a focused CELL the arrows walk the grid.
			event.preventDefault();
			event.stopPropagation();
			const root = event.currentTarget.closest(`[data-card-table="${tableId}"]`);
			root?.querySelector(`[data-cell-row="${rowIndex + move[0]}"][data-cell-col="${colIndex + move[1]}"]`)?.focus();
			return;
		}
		if (event.key === 'Enter' || event.key === 'F2') {
			event.preventDefault();
			event.stopPropagation();
			if (editable) onOpen?.();
			else if (editability?.opensItem) onOpenItem?.();
		}
	};

	const handleContextMenu = (event) => {
		if (!menuOn) return;
		// A long press already opened it; the contextmenu event that follows is the same gesture.
		if (press.current.fired) { event.preventDefault(); return; }
		if (event.target?.closest?.(FORM_CONTROLS)) return;
		// A keyboard-opened contextmenu event carries no pointer: sit under the cell.
		const fromKeyboard = event.clientX === 0 && event.clientY === 0;
		const [x, y] = fromKeyboard ? cellCorner() : [event.clientX, event.clientY];
		if (openMenu(x, y)) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const stopPress = () => {
		clearTimeout(press.current.timer);
		press.current.timer = null;
	};
	const handleTouchStart = (event) => {
		stopPress();
		press.current.fired = false;
		if (!menuOn || event.touches.length !== 1 || event.target?.closest?.(FORM_CONTROLS)) return;
		const { clientX, clientY } = event.touches[0];
		press.current.x = clientX;
		press.current.y = clientY;
		press.current.timer = setTimeout(() => {
			press.current.timer = null;
			if (openMenu(clientX, clientY)) {
				press.current.fired = true;
				// The tap that ends this press must not edit the cell or select the row.
				setTimeout(() => { press.current.fired = false; }, 700);
			}
		}, LONG_PRESS_MS);
	};
	const handleTouchMove = (event) => {
		const touch = event.touches[0];
		if (touch && Math.hypot(touch.clientX - press.current.x, touch.clientY - press.current.y) > LONG_PRESS_SLOP) stopPress();
	};

	const handleClick = (event) => {
		if (press.current.fired) {
			press.current.fired = false;
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		if (editable) {
			event.stopPropagation();
			onOpen?.();
		} else if (editability?.opensItem) {
			event.stopPropagation();
			onOpenItem?.();
		}
	};

	// The ⋯ is drawn only while the cell is hovered or focused, and only when
	// there is something behind it.
	const showMore = menuOn && active && menu.canOpen({ item, columnKey });

	return (
		<div
			ref={ref}
			className={classes}
			tabIndex={0}
			data-cell-row={rowIndex}
			data-cell-col={colIndex}
			title={reason || undefined}
			aria-readonly={reason ? true : undefined}
			aria-busy={pending || undefined}
			aria-keyshortcuts={showMore ? 'Shift+F10' : undefined}
			onKeyDown={handleKeyDown}
			onContextMenu={handleContextMenu}
			onTouchStart={menuOn ? handleTouchStart : undefined}
			onTouchMove={menuOn ? handleTouchMove : undefined}
			onTouchEnd={menuOn ? stopPress : undefined}
			onTouchCancel={menuOn ? stopPress : undefined}
			onMouseEnter={menuOn ? () => setActive(true) : undefined}
			onMouseLeave={menuOn ? (event) => setActive(event.currentTarget.contains(document.activeElement)) : undefined}
			onFocus={menuOn ? () => setActive(true) : undefined}
			onBlur={menuOn ? (event) => { if (!event.currentTarget.contains(event.relatedTarget)) setActive(false); } : undefined}
			onClick={handleClick}
			role="gridcell"
		>
			{children}
			{pending ? <Spin size="small" className="card-table__spinner" /> : null}
			{showMore ? (
				<button
					type="button"
					tabIndex={-1}
					className="card-table__cell-more"
					aria-label="Value actions"
					aria-haspopup="menu"
					onClick={(event) => {
						event.stopPropagation();
						const rect = event.currentTarget.getBoundingClientRect();
						openMenu(rect.left, rect.bottom);
					}}
				>
					<KEBAB aria-hidden="true" />
				</button>
			) : null}
		</div>
	);
}
